import { useSocketIOContext, type ConnectionStatus, type ConnectionEvent } from "@/contexts/socketIo";
import { ToolTip } from "@/components/ui/toolTip";
import clsx from "clsx";
import { useMemo } from "react";
import { CableIcon, UnplugIcon, WifiOffIcon } from "lucide-react";

export type OnlineOfflineIndicatorProps = {
  className?: string
}

const statusLabel: Record<ConnectionStatus, string> = {
  'connected': 'Online',
  'reconnecting': 'Reconnecting…',
  'browser-offline': 'You are offline',
  'server-unreachable': 'Server unreachable',
  'unstable': 'Connection unstable',
};

const statusColor: Record<ConnectionStatus, string> = {
  'connected': 'text-green-500',
  'reconnecting': 'text-yellow-500',
  'browser-offline': 'text-yellow-500',
  'server-unreachable': 'text-red-500',
  'unstable': 'text-yellow-500',
};

const ringColor: Record<ConnectionStatus, string> = {
  'connected': 'border-green-500/50',
  'reconnecting': 'border-yellow-500',
  'browser-offline': 'border-yellow-500',
  'server-unreachable': 'border-red-500',
  'unstable': 'border-yellow-500',
};

export const OnlineOfflineIndicator = (props: OnlineOfflineIndicatorProps) => {
  const { isConnected, connectionStatus, showRecentReconnect } = useSocketIOContext();
  const showRing = !isConnected || showRecentReconnect || connectionStatus === 'unstable';
  const isPulsating = connectionStatus === 'reconnecting' || connectionStatus === 'unstable';

  const icon = connectionStatus === 'browser-offline'
    ? <WifiOffIcon className={clsx("w-4 h-4", statusColor[connectionStatus])} />
    : !isConnected && connectionStatus !== 'unstable'
      ? <UnplugIcon className={clsx("w-4 h-4", statusColor[connectionStatus])} />
      : <CableIcon className={clsx("w-4 h-4", statusColor[connectionStatus])} />;

  return (
    <ToolTip text={<OnlineOfflineTooltipContent />} placement="right-start" textClassName="bg-black px-2 py-1 text-white rounded" addTextClassName=" whitespace-normal">
      <div className={clsx("relative flex items-center justify-center", props.className)}>
        {showRing && (
          <span
            className={clsx(
              "absolute inset-[-3px] rounded-full border-2",
              ringColor[connectionStatus],
              { "animate-pulse": isPulsating }
            )}
          />
        )}
        {icon}
      </div>
    </ToolTip>
  );
}

export const OnlineOfflineLabelIndicator = (props: OnlineOfflineIndicatorProps) => {
  const { connectionStatus } = useSocketIOContext();
  return (
    <div className={`flex items-center justify-center ${props.className || ""}`}>
      <span className={statusColor[connectionStatus]}>
        {statusLabel[connectionStatus]}
      </span>
    </div>
  )
}

type ConnectionQuality = 'Excellent' | 'Good' | 'Fair' | 'Poor';

const QUALITY_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

function computeConnectionQuality(history: ConnectionEvent[], isConnected: boolean): ConnectionQuality {
  const now = Date.now();
  const windowStart = now - QUALITY_WINDOW_MS;

  // Filter to events within the window
  const events = history.filter(e => e.timestamp >= windowStart && (e.type === 'connect' || e.type === 'disconnect'));

  if (events.length === 0) {
    return isConnected ? 'Excellent' : 'Poor';
  }

  // Walk through events and sum disconnected time within the window
  let disconnectedMs = 0;
  let lastDisconnectAt: number | null = null;

  // Determine initial state at windowStart by looking at the last event before the window
  const beforeWindow = history.filter(e => e.timestamp < windowStart && (e.type === 'connect' || e.type === 'disconnect'));
  const lastBefore = beforeWindow.length > 0 ? beforeWindow[beforeWindow.length - 1] : null;
  if (lastBefore?.type === 'disconnect') {
    lastDisconnectAt = windowStart; // Was disconnected at start of window
  }

  for (const event of events) {
    if (event.type === 'disconnect') {
      lastDisconnectAt = event.timestamp;
    } else if (event.type === 'connect' && lastDisconnectAt !== null) {
      disconnectedMs += event.timestamp - lastDisconnectAt;
      lastDisconnectAt = null;
    }
  }

  // If still disconnected, count up to now
  if (lastDisconnectAt !== null) {
    disconnectedMs += now - lastDisconnectAt;
  }

  const ratio = disconnectedMs / QUALITY_WINDOW_MS;

  if (ratio === 0) return 'Excellent';
  if (ratio < 0.02) return 'Good';
  if (ratio < 0.10) return 'Fair';
  return 'Poor';
}

const qualityColor: Record<ConnectionQuality, string> = {
  'Excellent': 'text-green-400',
  'Good': 'text-green-300',
  'Fair': 'text-amber-400',
  'Poor': 'text-red-400',
};

export const OnlineOfflineTooltipContent = () => {
  const { connectionStatus, reconnectAttempt, lastError, connectionHistory, isConnected } = useSocketIOContext();
  
  const recentEvents = connectionHistory.slice(-5);
  const quality = useMemo(() => computeConnectionQuality(connectionHistory, isConnected), [connectionHistory, isConnected]);

  return (
    <div className="flex flex-col gap-1.5 text-xs min-w-[180px] max-w-[280px] max-h-[300px] overflow-y-auto">
      <div className="font-medium">{statusLabel[connectionStatus]}</div>

      <div className="text-zinc-400">
        Connection: <span className={qualityColor[quality]}>{quality}</span>
      </div>
      
      {reconnectAttempt > 0 && (
        <div className="text-zinc-400">
          Reconnect attempts: {reconnectAttempt}
        </div>
      )}

      {lastError && (
        <div className="text-zinc-400 break-words">
          Last error: {lastError}
        </div>
      )}

      {recentEvents.length > 0 && (
        <div className="border-t border-zinc-700 pt-1.5 mt-0.5">
          <div className="text-zinc-500 mb-1">Recent events</div>
          {recentEvents.map((event, i) => (
            <div key={i} className="flex justify-between gap-2 text-zinc-400">
              <span>{event.type}</span>
              <span className="whitespace-nowrap">{new Date(event.timestamp).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}